/**
 * Lightweight client-side export & print helpers for ΚΩΔΙΚΟΙ.
 * No external dependencies — uses the browser print dialog (which
 * supports "Save as PDF") and CSV with a BOM for Excel-friendly UTF-8.
 */

function escapeHtml(s) {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function fmtGR(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(
      d.getFullYear()
    ).slice(-2)}`;
  } catch {
    return iso;
  }
}

function itemsLabel(c) {
  if (Array.isArray(c.items) && c.items.length) {
    return c.items
      .map((it) => (it.qty > 1 ? `${it.kind} x${it.qty}` : it.kind))
      .join(" + ");
  }
  return c.kind || "";
}

/** Print a single ΚΩΔΙΚΟΙ card via the browser print dialog. */
export function printCodeCard(c) {
  const html = `<!doctype html>
<html lang="el">
<head>
  <meta charset="utf-8" />
  <title>ΚΩΔΙΚΟΙ — ${escapeHtml(c.customer || "")}</title>
  <style>
    @page { size: A4; margin: 18mm; }
    body {
      font-family: "Segoe UI", Arial, sans-serif;
      color: #222;
      margin: 0;
    }
    h1 {
      font-size: 22pt;
      margin: 0 0 6pt 0;
      letter-spacing: 0.5pt;
    }
    .meta {
      color: #555;
      font-size: 10pt;
      margin-bottom: 18pt;
    }
    table.fields {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 16pt;
    }
    table.fields td {
      padding: 7pt 8pt;
      vertical-align: top;
      border-bottom: 1px solid #ddd;
      font-size: 11pt;
    }
    table.fields td.label {
      width: 38mm;
      color: #555;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5pt;
      font-size: 9pt;
    }
    .codes-block, .notes-block {
      border: 1px solid #444;
      border-radius: 4pt;
      padding: 10pt;
      margin-top: 8pt;
      white-space: pre-wrap;
      word-wrap: break-word;
    }
    .codes-block { font-family: "Consolas", "Courier New", monospace; font-size: 11pt; background: #f5f5f5; }
    .notes-block { font-size: 11pt; }
    .section-title {
      font-size: 10pt;
      text-transform: uppercase;
      letter-spacing: 1pt;
      font-weight: 700;
      color: #555;
      margin-top: 18pt;
    }
    .footer {
      position: fixed;
      bottom: 8mm;
      left: 18mm;
      right: 18mm;
      font-size: 8pt;
      color: #888;
      display: flex;
      justify-content: space-between;
      border-top: 1px solid #ccc;
      padding-top: 4pt;
    }
  </style>
</head>
<body>
  <h1>${escapeHtml(c.customer || "—")}</h1>
  <div class="meta">ΕΓΓΡΑΦΗ ΚΩΔΙΚΩΝ ΠΡΟΓΡΑΜΜΑΤΟΣ</div>
  <table class="fields">
    <tr><td class="label">ΤΥΠΟΣ</td><td>${escapeHtml(itemsLabel(c))}</td></tr>
    <tr><td class="label">ΚΩΔΙΚΟΣ</td><td>${escapeHtml(c.code || "")}</td></tr>
    <tr><td class="label">ΤΗΛΕΦΩΝΟ</td><td>${escapeHtml(c.phone || "")}</td></tr>
    <tr><td class="label">ΕΝΕΡΓΟΠΟΙΗΣΗ</td><td>${escapeHtml(fmtGR(c.activation_date))}</td></tr>
    <tr><td class="label">ΛΗΞΗ</td><td>${escapeHtml(fmtGR(c.expiry_date))}</td></tr>
  </table>
  ${
    (c.codes || "").trim()
      ? `<div class="section-title">ΚΩΔΙΚΟΙ ΠΡΟΓΡΑΜΜΑΤΟΣ</div>
         <div class="codes-block">${escapeHtml(c.codes)}</div>`
      : ""
  }
  ${
    (c.notes || "").trim()
      ? `<div class="section-title">ΣΗΜΕΙΩΣΕΙΣ</div>
         <div class="notes-block">${escapeHtml(c.notes)}</div>`
      : ""
  }
  <div class="footer">
    <span>PinBoard tameiakes.gr — ΚΩΔΙΚΟΙ</span>
    <span>${new Date().toLocaleDateString("el-GR")} ${new Date().toLocaleTimeString("el-GR")}</span>
  </div>
  <script>window.onload = () => { setTimeout(() => window.print(), 200); };</script>
</body>
</html>`;

  const w = window.open("", "_blank", "width=900,height=900");
  if (!w) return;
  w.document.open();
  w.document.write(html);
  w.document.close();
}

/** Open the print dialog with the whole ΚΩΔΙΚΟΙ table rendered for A4 landscape.
 *  The user can choose "Save as PDF" to export as PDF. */
export function printCodesTable(items) {
  const rows = items
    .map(
      (c) => `
    <tr>
      <td>${escapeHtml(c.customer || "")}</td>
      <td>${escapeHtml(itemsLabel(c))}</td>
      <td>${escapeHtml(c.code || "")}</td>
      <td>${escapeHtml(c.phone || "")}</td>
      <td>${escapeHtml(fmtGR(c.activation_date))}</td>
      <td>${escapeHtml(fmtGR(c.expiry_date))}</td>
      <td class="mono">${escapeHtml(c.codes || "")}</td>
      <td>${escapeHtml(c.notes || "")}</td>
    </tr>`
    )
    .join("");

  const html = `<!doctype html>
<html lang="el">
<head>
  <meta charset="utf-8" />
  <title>ΚΩΔΙΚΟΙ — ${items.length} ΕΓΓΡΑΦΕΣ</title>
  <style>
    @page { size: A4 landscape; margin: 12mm; }
    body { font-family: "Segoe UI", Arial, sans-serif; color: #222; margin: 0; }
    h1 { font-size: 16pt; margin: 0 0 6pt 0; }
    .meta { color: #555; font-size: 10pt; margin-bottom: 10pt; }
    table { width: 100%; border-collapse: collapse; font-size: 8.5pt; }
    th, td {
      border: 1px solid #888;
      padding: 4pt 6pt;
      vertical-align: top;
      text-align: left;
      word-wrap: break-word;
    }
    th {
      background: #222;
      color: #fff;
      text-transform: uppercase;
      letter-spacing: 0.5pt;
      font-size: 8pt;
    }
    tr:nth-child(even) td { background: #fafafa; }
    .mono { font-family: "Consolas","Courier New", monospace; font-size: 8pt; white-space: pre-wrap; }
    .footer { margin-top: 8pt; font-size: 8pt; color: #888; text-align: right; }
  </style>
</head>
<body>
  <h1>ΚΩΔΙΚΟΙ ΠΡΟΓΡΑΜΜΑΤΩΝ</h1>
  <div class="meta">${items.length} ΕΓΓΡΑΦΕΣ · ${new Date().toLocaleDateString("el-GR")}</div>
  <table>
    <thead>
      <tr>
        <th>ΠΕΛΑΤΗΣ</th>
        <th>ΤΥΠΟΣ</th>
        <th>ΚΩΔΙΚΟΣ</th>
        <th>ΤΗΛΕΦΩΝΟ</th>
        <th>ΕΝΕΡΓΟΠΟΙΗΣΗ</th>
        <th>ΛΗΞΗ</th>
        <th>ΚΩΔΙΚΟΙ ΠΡΟΓΡ.</th>
        <th>ΣΗΜΕΙΩΣΕΙΣ</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="footer">PinBoard tameiakes.gr</div>
  <script>window.onload = () => { setTimeout(() => window.print(), 200); };</script>
</body>
</html>`;
  const w = window.open("", "_blank", "width=1200,height=900");
  if (!w) return;
  w.document.open();
  w.document.write(html);
  w.document.close();
}

/** Trigger download of a CSV file (UTF-8 with BOM so Excel opens Greek correctly). */
function csvEscape(v) {
  const s = v == null ? "" : String(v);
  if (/[",\n;]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function exportCodesCSV(items) {
  const headers = [
    "ΠΕΛΑΤΗΣ",
    "ΤΥΠΟΣ",
    "ΚΩΔΙΚΟΣ",
    "ΤΗΛΕΦΩΝΟ",
    "ΕΝΕΡΓΟΠΟΙΗΣΗ",
    "ΛΗΞΗ",
    "ΚΩΔΙΚΟΙ ΠΡΟΓΡΑΜΜΑΤΟΣ",
    "ΣΗΜΕΙΩΣΕΙΣ",
  ];
  const lines = [headers.map(csvEscape).join(";")];
  items.forEach((c) => {
    lines.push(
      [
        c.customer || "",
        itemsLabel(c),
        c.code || "",
        c.phone || "",
        fmtGR(c.activation_date),
        fmtGR(c.expiry_date),
        c.codes || "",
        c.notes || "",
      ]
        .map(csvEscape)
        .join(";")
    );
  });
  const csv = "\ufeff" + lines.join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const ts = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `kodikoi_${ts}.csv`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 0);
}
